/**
 * Heatmap types and CAM grid API response conversion.
 * Data is loaded from the API (grid-vehicle-counts, grid-speed-values).
 */

export type HeatmapPoint = [number, number, number]; // [lat, lng, intensity]

export type HeatmapMetric = 'counter' | 'speed_value';

/**
 * ETSI CAM SpeedValue / grid-speed-values `v` is stored as 0.01 m/s (no API conversion).
 * km/h = v × 0.036  (same as CamInfoTooltip / Cam list).
 * Examples: 1456 → 52.4 km/h, 2198 → 79.1 km/h, 3800 → 136.8 km/h.
 */
export const ETSI_SPEED_TO_KMH = 0.036;

export function etsiSpeedToKmh(speedValue: number): number {
  return speedValue * ETSI_SPEED_TO_KMH;
}

/** API response: keys @lat,lng|quarter_hour_iso; value = { v, q, h } with q/h as quarter-hour and hour ISO */
export type GridCamApiResponse = Record<string, { v: string; q: string; h: string }>;

/** Row shape for dashboard slot charts (same as store heatmap.slotData) */
export type HeatmapSlotRow = {
  createdAt: number;
  counter: number;
  speedValue: number;
};

/** Merged grid row: both metrics per cell for client-side metric switching */
export type MergedGridRow = {
  lat: number;
  lng: number;
  counter: number;
  speedValue: number;
  createdAt: number;
};

/**
 * Parse API key "@lat,lng|quarter_hour_iso" into coords; returns null if invalid.
 */
function parseGridKey(key: string): { lat: number; lng: number } | null {
  if (!key.startsWith('@')) return null;
  const pipe = key.indexOf('|');
  const coordsPart = pipe >= 0 ? key.slice(1, pipe) : key.slice(1);
  const parts = coordsPart.split(',');
  if (parts.length !== 2) return null;
  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
}

/**
 * Merge the two metric responses into one array of rows (both metrics per cell per quarter hour).
 * Keys are @lat,lng|quarter_hour_iso; uses entry.q for createdAt.
 * Speed values are converted from ETSI units to km/h.
 */
export function mergeGridApiResponses(
  counterRes: GridCamApiResponse,
  speedRes: GridCamApiResponse,
): MergedGridRow[] {
  const allKeys = new Set(
    [...Object.keys(counterRes), ...Object.keys(speedRes)].filter((k) => k.startsWith('@')),
  );
  const rows: MergedGridRow[] = [];
  allKeys.forEach((key) => {
    const coords = parseGridKey(key);
    if (!coords) return;
    const c = counterRes[key];
    const s = speedRes[key];
    const counter = c && typeof c.v === 'string' ? parseFloat(c.v) : 0;
    const speedRaw = s && typeof s.v === 'string' ? parseFloat(s.v) : 0;
    const speedValue = Number.isNaN(speedRaw) ? 0 : etsiSpeedToKmh(speedRaw);
    const q = c?.q ?? s?.q;
    const createdAt = q ? Date.parse(q) : 0;
    if (Number.isNaN(createdAt)) return;
    rows.push({
      lat: coords.lat,
      lng: coords.lng,
      counter,
      speedValue,
      createdAt,
    });
  });
  return rows;
}

/**
 * Convert merged grid rows to HeatmapPoint[] for the active metric.
 */
export function mergedRowsToHeatmapPoints(rows: MergedGridRow[], metric: HeatmapMetric): HeatmapPoint[] {
  const getValue = (r: MergedGridRow): number =>
    metric === 'counter' ? r.counter : r.speedValue;
  return rows.map((r) => [r.lat, r.lng, getValue(r)] as HeatmapPoint);
}

/**
 * Convert merged grid rows to slotData for dashboard charts (both metrics per row).
 */
export function mergedRowsToSlotData(rows: MergedGridRow[]): HeatmapSlotRow[] {
  return rows.map((r) => ({
    createdAt: r.createdAt,
    counter: r.counter,
    speedValue: r.speedValue,
  }));
}

/**
 * Convert CAM grid API response to HeatmapPoint[].
 * Keys are @lat,lng|quarter_hour_iso; multiple entries per cell are aggregated:
 * counter = sum; speed_value = average (after converting ETSI → km/h).
 * When range [min, max] is provided (min < max), only entries with q in that range are included.
 */
export function gridApiResponseToHeatmapPoints(
  response: GridCamApiResponse,
  metric: HeatmapMetric,
  range?: [number, number],
): HeatmapPoint[] {
  const filterByRange = range != null && range[0] < range[1];
  const rangeMin = filterByRange ? range[0] : 0;
  const rangeMax = filterByRange ? range[1] : 0;
  const cellMap = new Map<string, { lat: number; lng: number; values: number[] }>();
  Object.entries(response).forEach(([key, entry]) => {
    if (!entry || typeof entry.v !== 'string') return;
    if (filterByRange) {
      const createdAt = Date.parse(entry.q);
      if (Number.isNaN(createdAt) || createdAt < rangeMin || createdAt > rangeMax) return;
    }
    const coords = parseGridKey(key);
    if (!coords) return;
    let value = parseFloat(entry.v);
    if (Number.isNaN(value)) return;
    if (metric === 'speed_value') value = etsiSpeedToKmh(value);
    const cellKey = `${coords.lat},${coords.lng}`;
    const existing = cellMap.get(cellKey);
    if (existing) {
      existing.values.push(value);
    } else {
      cellMap.set(cellKey, { lat: coords.lat, lng: coords.lng, values: [value] });
    }
  });
  const aggregate = (values: number[]): number => {
    if (metric === 'speed_value') {
      return values.length === 0 ? 0 : values.reduce((a, b) => a + b, 0) / values.length;
    }
    return values.reduce((a, b) => a + b, 0);
  };
  return Array.from(cellMap.entries(), ([, c]) => [
    c.lat,
    c.lng,
    aggregate(c.values),
  ] as HeatmapPoint);
}

/**
 * Min/max average speed (km/h) from raw grid samples — never sums across cells or time.
 * Each API `v` is already a cell×quarter-hour speed; we only convert ETSI → km/h.
 * Optional range limits which samples are considered (for "scale to visible").
 */
export function gridApiResponseToSpeedValueRange(
  response: GridCamApiResponse,
  range?: [number, number],
): { min: number; max: number } | null {
  const filterByRange = range != null && range[0] < range[1];
  const rangeMin = filterByRange ? range[0] : 0;
  const rangeMax = filterByRange ? range[1] : 0;
  let min = Infinity;
  let max = -Infinity;
  let found = false;
  Object.values(response).forEach((entry) => {
    if (!entry || typeof entry.v !== 'string') return;
    if (filterByRange) {
      const createdAt = Date.parse(entry.q);
      if (Number.isNaN(createdAt) || createdAt < rangeMin || createdAt > rangeMax) return;
    }
    const kmh = etsiSpeedToKmh(parseFloat(entry.v));
    if (Number.isNaN(kmh) || kmh <= 0) return;
    found = true;
    if (kmh < min) min = kmh;
    if (kmh > max) max = kmh;
  });
  return found ? { min, max } : null;
}

/**
 * Convert CAM grid API response to slotData rows for dashboard charts.
 * One row per (cell, quarter hour); createdAt from entry.q; use entry.h to group by hour.
 * Speed values are converted from ETSI units to km/h.
 */
export function gridApiResponseToSlotData(
  response: GridCamApiResponse,
  metric: HeatmapMetric,
): HeatmapSlotRow[] {
  return Object.entries(response).reduce<HeatmapSlotRow[]>((acc, [, entry]) => {
    if (!entry || typeof entry.v !== 'string') return acc;
    const createdAt = Date.parse(entry.q);
    if (Number.isNaN(createdAt)) return acc;
    let value = parseFloat(entry.v);
    if (Number.isNaN(value)) return acc;
    if (metric === 'speed_value') value = etsiSpeedToKmh(value);
    const row: HeatmapSlotRow = {
      createdAt,
      counter: metric === 'counter' ? value : 0,
      speedValue: metric === 'speed_value' ? value : 0,
    };
    return [...acc, row];
  }, []);
}
